import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { PermissionService } from '../permission';
import { SelfaiAuthService } from './selfai-auth.service';

@Injectable({
  providedIn: 'root',
})
export class AppAuthGuard implements CanActivate {
  private authenticated: boolean;
  private roles: string[];

  constructor(
    private readonly router: Router,
    private readonly selfaiAuthService: SelfaiAuthService,
    private readonly permissionService: PermissionService,
  ) {}

  /**
   * Authentication and authorization guard.
   * Contains logic for both user authentication (login check) and authorization (role-based access control).
   * Redirects unauthenticated users to login and unauthorized users to access denied page.
   */
  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    try {
      this.authenticated = this.selfaiAuthService.getProvider().isLoggedIn();

      this.roles = await Promise.all([
        firstValueFrom(this.permissionService.pipelinePermissionsLoadedObs$.pipe(filter((loaded) => loaded === true))),
        firstValueFrom(this.permissionService.biPermissionsLoadedObs$.pipe(filter((loaded) => loaded === true))),
      ]).then(() => this.permissionService.getPermissions());

      return await this.isAccessAllowed(route);
    } catch (error) {
      throw new Error('An error happened during access validation. Details:' + error);
    }
  }

  async isAccessAllowed(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    // Force the user to log in if currently unauthenticated.
    if (!this.authenticated) {
      await this.selfaiAuthService.getProvider().login({
        redirectUri: window.location.href,
      });
    }

    // Get the roles required from the route.
    const requiredRoles = route.data['roles'];

    let granted = false;

    // Allow the user to to proceed if no additional roles are required to access the route.
    if (!(requiredRoles instanceof Array) || requiredRoles.length === 0) {
      granted = true;

      return granted;
    }

    // Allow the user to proceed if all the required roles are present.
    granted = requiredRoles.some((role) => this.roles.includes(role));

    // Routing user into permission denied view if don't have necessary roles.
    if (!granted) {
      await this.router.navigate(['access-denied']);
    }

    return granted;
  }
}
